import { Injectable } from '@angular/core';
import { Observable, from, throwError, forkJoin, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { initializeApp, FirebaseApp, getApp } from 'firebase/app';
import { getFirestore, Firestore, collection, getDocs, where, query, orderBy, Timestamp, doc, updateDoc, addDoc, deleteDoc } from 'firebase/firestore';
import { getAuth, Auth, onAuthStateChanged } from 'firebase/auth';
import { LoadingSpinnerService } from './loading-spinner.service';
import { DateFormatterService } from './date-formatter.service';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Router } from '@angular/router';
import { CognitoCredentialsService } from './cognito-credentials.service';

// TypeScript interfaces
export interface Birthday {
  id: string;
  birthdayName: string;
  birthdayYears: number;
  numberOfMagnets: number;
  note: string;
  startTime: string;
  endTime: string;
  locationId: string;
  roomId: string;
  orderStatus: string;
  playgroundId: string;
  createdDate: string;
  email: string;
  number: string;
  services: string[];
  uid: string;
  orgId: string;
  savedLocallyByUser: boolean;
  deliveryAddress: string;
  organizationName: string;
  staffId: string;
  cancellationMessage?: string;
  cancelledAt?: string;
  paymentLink?: string;
  celebrantId?: string;
  adminStoryUrl?: string;
}

export interface Playground {
  id: string;
  uid: string;
  businessId?: string;
  [key: string]: any;
}

export interface Location {
  id: string;
  name: string;
  playgroundId: string;
  city?: string;
  rooms: Room[];
  [key: string]: any;
}

export interface Room {
  id: string;
  name: string;
  [key: string]: any;
}

export interface ReservedSlot {
  start: string;
  end: string;
}

export interface BirthdayData {
  birthdayName: string;
  birthdayYears: number;
  numberOfMagnets: number;
  note: string;
  adminStoryUrl?: string;
  startTime: Timestamp;
  endTime: Timestamp;
  locationId: string;
  roomId: string;
  orderStatus: string;
  playgroundId: string;
  createdDate: Timestamp;
  email: string;
  number: string;
  services: string[];
  uid: string;
  orgId: string;
  savedLocallyByUser: boolean;
  deliveryAddress: string;
  organizationName: string;
  staffId: string;
  celebrantId?: string;
}

/** Firestore allows max 30 values in an 'in' query. */
const FIRESTORE_IN_LIMIT = 30;

@Injectable({ providedIn: 'root' })
export class OrdersApiService {
  private secondaryApp!: FirebaseApp;
  private db!: Firestore;
  private auth!: Auth;
  private mpIds: string[] = [];
  private businessId: string = '';

  /** Split array into chunks of at most FIRESTORE_IN_LIMIT for batched 'in' queries. */
  private chunkIds(ids: string[]): string[][] {
    if (ids.length === 0) return [];
    const chunks: string[][] = [];
    for (let i = 0; i < ids.length; i += FIRESTORE_IN_LIMIT) {
      chunks.push(ids.slice(i, i + FIRESTORE_IN_LIMIT));
    }
    return chunks;
  }

  constructor(
    private loadingSpinnerService: LoadingSpinnerService,
    private dateFormatter: DateFormatterService,
    private afAuth: AngularFireAuth,
    private router: Router,
    private cognitoCredentials: CognitoCredentialsService
  ) {}

  initialize(mpIds: string[], businessId: string): void {
    this.mpIds = mpIds;
    this.businessId = businessId;

    // Initialize Firebase
    try {
      this.secondaryApp = getApp('gulli-orders');
    } catch (error) {
      this.secondaryApp = initializeApp(environment.firebaseSecondary, 'gulli-orders');
    }

    this.db = getFirestore(this.secondaryApp);
    this.auth = getAuth(this.secondaryApp);

    // Check if user exists in secondary auth, logout if not
    onAuthStateChanged(this.auth, (user) => {
      if (!user) {
        // User doesn't exist in secondary auth, logout from primary auth
        // Clear Cognito credentials cache
        this.cognitoCredentials.clearCache();
        
        this.afAuth.signOut().then(() => {
          localStorage.clear();
          this.router.navigate(['/login']);
        }).catch((error) => {
          console.error('Logout failed', error);
        });
      }
    });
  }

  // Data fetching methods
  getBirthdays(date: string): Observable<Birthday[]> {
    this.loadingSpinnerService.show();

    const startOfDay = new Date(date);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);

    const chunks = this.chunkIds(this.mpIds);
    if (chunks.length === 0) {
      this.loadingSpinnerService.hide();
      return of([]);
    }

    const birthdaysRef = collection(this.db, 'appointments');
    const queries = chunks.map(ids => query(
      birthdaysRef,
      where('playgroundId', 'in', ids),
      where('startTime', '>=', Timestamp.fromDate(startOfDay)),
      where('startTime', '<=', Timestamp.fromDate(endOfDay))
    ));

    return forkJoin(queries.map(q => from(getDocs(q)))).pipe(
      map(snapshots => {
        const birthdays: Birthday[] = [];
        for (const snapshot of snapshots) {
          for (const doc of snapshot.docs) {
            const data = doc.data();
            if (data['startTime'] instanceof Timestamp && data['endTime'] instanceof Timestamp) {
              data['startTime'] = data['startTime'].toDate().toISOString();
              data['endTime'] = data['endTime'].toDate().toISOString();
            }
            birthdays.push({ id: doc.id, ...data } as Birthday);
          }
        }
        birthdays.sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());
        this.loadingSpinnerService.hide();
        return birthdays;
      }),
      catchError(error => {
        console.error('Error fetching Birthdays from Firestore:', error);
        this.loadingSpinnerService.hide();
        return throwError(() => error);
      })
    );
  }

  /**
   * Fetch birthdays by createdDate instead of event startTime.
   * Used by Orders Admin where listings are grouped/filtered by createdDate.
   */
  getBirthdaysByCreatedDate(date: string): Observable<Birthday[]> {
    this.loadingSpinnerService.show();

    const startOfDay = new Date(date);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);

    const chunks = this.chunkIds(this.mpIds);
    if (chunks.length === 0) {
      this.loadingSpinnerService.hide();
      return of([]);
    }

    const birthdaysRef = collection(this.db, 'appointments');
    const queries = chunks.map(ids => query(
      birthdaysRef,
      where('playgroundId', 'in', ids),
      where('createdDate', '>=', Timestamp.fromDate(startOfDay)),
      where('createdDate', '<=', Timestamp.fromDate(endOfDay))
    ));

    return forkJoin(queries.map(q => from(getDocs(q)))).pipe(
      map(snapshots => {
        const birthdays: Birthday[] = [];
        for (const snapshot of snapshots) {
          for (const doc of snapshot.docs) {
            const data = doc.data();
            if (data['startTime'] instanceof Timestamp) data['startTime'] = data['startTime'].toDate().toISOString();
            if (data['endTime'] instanceof Timestamp) data['endTime'] = data['endTime'].toDate().toISOString();
            if (data['createdDate'] instanceof Timestamp) data['createdDate'] = data['createdDate'].toDate().toISOString();
            birthdays.push({ id: doc.id, ...data } as Birthday);
          }
        }
        birthdays.sort((a, b) => {
          const aTime = a.createdDate ? new Date(a.createdDate).getTime() : 0;
          const bTime = b.createdDate ? new Date(b.createdDate).getTime() : 0;
          return bTime - aTime;
        });
        this.loadingSpinnerService.hide();
        return birthdays;
      }),
      catchError(error => {
        console.error('Error fetching Birthdays by createdDate from Firestore:', error);
        this.loadingSpinnerService.hide();
        return throwError(() => error);
      })
    );
  }

  getBirthdaysRange(startDate: string, endDate: string): Observable<Birthday[]> {
    this.loadingSpinnerService.show();

    const startOfStartDay = new Date(startDate);
    startOfStartDay.setHours(0, 0, 0, 0);
    const endOfEndDay = new Date(endDate);
    endOfEndDay.setHours(23, 59, 59, 999);

    const chunks = this.chunkIds(this.mpIds);
    if (chunks.length === 0) {
      this.loadingSpinnerService.hide();
      return of([]);
    }

    const birthdaysRef = collection(this.db, 'appointments');
    const queries = chunks.map(ids => query(
      birthdaysRef,
      where('playgroundId', 'in', ids),
      where('startTime', '>=', Timestamp.fromDate(startOfStartDay)),
      where('startTime', '<=', Timestamp.fromDate(endOfEndDay))
    ));

    return forkJoin(queries.map(q => from(getDocs(q)))).pipe(
      map(snapshots => {
        const birthdays: Birthday[] = [];
        for (const snapshot of snapshots) {
          for (const doc of snapshot.docs) {
            const data = doc.data();
            if (data['startTime'] instanceof Timestamp && data['endTime'] instanceof Timestamp) {
              data['startTime'] = data['startTime'].toDate().toISOString();
              data['endTime'] = data['endTime'].toDate().toISOString();
            }
            birthdays.push({ id: doc.id, ...data } as Birthday);
          }
        }
        birthdays.sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());
        this.loadingSpinnerService.hide();
        return birthdays;
      }),
      catchError(error => {
        console.error('Error fetching Birthdays (range) from Firestore:', error);
        this.loadingSpinnerService.hide();
        return throwError(() => error);
      })
    );
  }

  /**
   * Fetch birthdays by createdDate within a date range.
   * Used by Orders Admin for month/custom range filters on createdDate.
   */
  getBirthdaysRangeByCreatedDate(startDate: string, endDate: string): Observable<Birthday[]> {
    this.loadingSpinnerService.show();

    const startOfStartDay = new Date(startDate);
    startOfStartDay.setHours(0, 0, 0, 0);
    const endOfEndDay = new Date(endDate);
    endOfEndDay.setHours(23, 59, 59, 999);

    const chunks = this.chunkIds(this.mpIds);
    if (chunks.length === 0) {
      this.loadingSpinnerService.hide();
      return of([]);
    }

    const birthdaysRef = collection(this.db, 'appointments');
    const queries = chunks.map(ids => query(
      birthdaysRef,
      where('playgroundId', 'in', ids),
      where('createdDate', '>=', Timestamp.fromDate(startOfStartDay)),
      where('createdDate', '<=', Timestamp.fromDate(endOfEndDay))
    ));

    return forkJoin(queries.map(q => from(getDocs(q)))).pipe(
      map(snapshots => {
        const birthdays: Birthday[] = [];
        for (const snapshot of snapshots) {
          for (const doc of snapshot.docs) {
            const data = doc.data();
            if (data['startTime'] instanceof Timestamp) data['startTime'] = data['startTime'].toDate().toISOString();
            if (data['endTime'] instanceof Timestamp) data['endTime'] = data['endTime'].toDate().toISOString();
            if (data['createdDate'] instanceof Timestamp) data['createdDate'] = data['createdDate'].toDate().toISOString();
            birthdays.push({ id: doc.id, ...data } as Birthday);
          }
        }
        birthdays.sort((a, b) => {
          const aTime = a.createdDate ? new Date(a.createdDate).getTime() : 0;
          const bTime = b.createdDate ? new Date(b.createdDate).getTime() : 0;
          return bTime - aTime;
        });
        this.loadingSpinnerService.hide();
        return birthdays;
      }),
      catchError(error => {
        console.error('Error fetching Birthdays (range) by createdDate from Firestore:', error);
        this.loadingSpinnerService.hide();
        return throwError(() => error);
      })
    );
  }

  getManagedPlaygrounds(): Observable<Playground[]> {
    const chunks = this.chunkIds(this.mpIds);
    if (chunks.length === 0) return of([]);

    const playgroundsRef = collection(this.db, 'user_clients');
    const queries = chunks.map(ids => query(playgroundsRef, where('uid', 'in', ids)));

    return forkJoin(queries.map(q => from(getDocs(q)))).pipe(
      map(snapshots => {
        const playgrounds: Playground[] = [];
        for (const snapshot of snapshots) {
          for (const doc of snapshot.docs) {
            playgrounds.push({ id: doc.id, ...doc.data() } as Playground);
          }
        }
        return playgrounds;
      }),
      catchError(error => {
        console.error('Error fetching managed playgrounds:', error);
        return throwError(() => error);
      })
    );
  }

  getLocations(): Observable<Location[]> {
    const chunks = this.chunkIds(this.mpIds);
    if (chunks.length === 0) return of([]);

    const locationsRef = collection(this.db, 'playgrounds_locations');
    const queries = chunks.map(ids => query(locationsRef, where('playgroundId', 'in', ids)));

    return forkJoin(queries.map(q => from(getDocs(q)))).pipe(
      map(snapshots => {
        const locations: Location[] = [];
        for (const snapshot of snapshots) {
          for (const doc of snapshot.docs) {
            locations.push({ id: doc.id, ...doc.data() } as Location);
          }
        }
        return locations;
      }),
      catchError(error => {
        console.error('Error fetching locations:', error);
        return throwError(() => error);
      })
    );
  }

  getLocationsForPlayground(playgroundId: string): Observable<Location[]> {
    const locationsRef = collection(this.db, 'playgrounds_locations');
    const q = query(
      locationsRef,
      where('playgroundId', '==', playgroundId)
    );

    return from(getDocs(q)).pipe(
      map(querySnapshot => {
        return querySnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data
          } as Location;
        });
      }),
      catchError(error => {
        console.error('Error getting locations for playground:', error);
        return throwError(() => error);
      })
    );
  }

  getDeliveryDays(playgroundId: string): Observable<number> {
    // Step 1: Query playgrounds_locations collection for city parameter
    const locationsRef = collection(this.db, 'playgrounds_locations');
    const locationsQuery = query(
      locationsRef,
      where('playgroundId', '==', playgroundId)
    );
    return from(getDocs(locationsQuery)).pipe(
      switchMap(locationsSnapshot => {
        let city: string | null = null;
        if (!locationsSnapshot.empty) {
          const locationData = locationsSnapshot.docs[0].data();
          city = locationData['city'] || null;
        }

        // Step 2: Query delivery_info collection based on city or countryName
        const deliveryInfoRef = collection(this.db, 'delivery_info');
        let deliveryQuery;

        if (city) {
          // Query by city (isoCode)
          deliveryQuery = query(
            deliveryInfoRef,
            where('isoCode', '==', city)
          );
        } else {
          // Query by countryName from localStorage
          const countryName = localStorage.getItem('countryName');
          if (!countryName) {
            // console.log('No countryName found in localStorage and no city parameter');
            return from(Promise.resolve({ empty: true, docs: [] }));
          }
          deliveryQuery = query(
            deliveryInfoRef,
            where('countryName', '==', countryName)
          );
        }

        return from(getDocs(deliveryQuery));
      }),
      map(deliverySnapshot => {
        if (!deliverySnapshot.empty) {
          const deliveryData = deliverySnapshot.docs[0].data();
          const deliveryDays = deliveryData['deliveryDays'] || 0;
          return deliveryDays;
        } else {
          // console.log('No delivery info found for the given criteria');
          return 0;
        }
      }),
      catchError(error => {
        console.error('Error fetching delivery days:', error);
        return throwError(() => error);
      })
    );
  }

  getReservedSlots(playgroundId: string, locationId: string, roomId: string, date: string): Observable<ReservedSlot[]> {
    this.loadingSpinnerService.show();

    // Create date objects for start and end of day
    const dateObj = new Date(date);
    const startOfDay = new Date(dateObj);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(dateObj);
    endOfDay.setHours(23, 59, 59, 999);

    // Build the Firestore query as specified
    const appointmentsRef = collection(this.db, 'appointments');
    const q = query(
      appointmentsRef,
      where('playgroundId', '==', playgroundId),
      where('locationId', '==', locationId),
      where('roomId', '==', roomId),
      where('startTime', '>=', Timestamp.fromDate(startOfDay)),
      where('startTime', '<=', Timestamp.fromDate(endOfDay)),
      orderBy('startTime', 'asc')
    );

    return from(getDocs(q)).pipe(
      map(querySnapshot => {
        if (querySnapshot.docs.length > 0) {
          const reservedSlots = querySnapshot.docs.map(doc => {
            const data = doc.data();
            return {
              start: this.dateFormatter.formatTime(data['startTime'].toDate().toISOString()),
              end: this.dateFormatter.formatTime(data['endTime'].toDate().toISOString()),
            } as ReservedSlot;
          });
          this.loadingSpinnerService.hide();
          return reservedSlots;
        } else {
          this.loadingSpinnerService.hide();
          return [];
        }
      }),
      catchError(error => {
        console.error('Error fetching Reserved Birthdays:', error);
        this.loadingSpinnerService.hide();
        return throwError(() => error);
      })
    );
  }

  // CRUD operations
  createBirthday(birthdayData: BirthdayData): Observable<string> {
    const appointmentsRef = collection(this.db, 'appointments');
    
    return from(addDoc(appointmentsRef, birthdayData)).pipe(
      map(docRef => docRef.id),
      catchError(error => {
        console.error('Error creating birthday in Firebase:', error);
        return throwError(() => error);
      })
    );
  }

  updateBirthday(birthdayId: string, updateData: Partial<BirthdayData>): Observable<void> {
    const birthdayRef = doc(this.db, 'appointments', birthdayId);
    
    return from(updateDoc(birthdayRef, updateData)).pipe(
      map(() => void 0),
      catchError(error => {
        console.error('Error updating birthday in Firebase:', error);
        return throwError(() => error);
      })
    );
  }

  deleteBirthday(birthdayId: string): Observable<void> {
    const birthdayRef = doc(this.db, 'appointments', birthdayId);
    
    return from(deleteDoc(birthdayRef)).pipe(
      map(() => void 0),
      catchError(error => {
        console.error('Error deleting birthday from Firebase:', error);
        return throwError(() => error);
      })
    );
  }

  cancelBirthday(birthdayId: string, reason: string): Observable<void> {
    const updateData = {
      orderStatus: 'cancelled',
      cancellationMessage: reason,
      cancelledAt: Timestamp.fromDate(new Date())
    };

    return this.updateBirthday(birthdayId, updateData);
  }
}


